import { useState } from 'react';
import { useParams } from 'react-router';
import { useAdmins, useRevokeAdmin } from '@/features/admin-invite/api';
import { useAuthStore } from '@/features/auth/auth.store';
import { InviteModal } from '@/features/admin-invite/invite-modal';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

export const handle = {
  breadcrumb: 'Administradores',
};

export function AdministradoresRoute() {
  const { condoId = '' } = useParams<{ condoId: string }>();
  const session = useAuthStore((s) => s.session);
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data, isLoading } = useAdmins(condoId);
  const revoke = useRevokeAdmin(condoId);

  if (!session?.isSuperAdmin) {
    return (
      <PageContainer width="wide">
        <PageHeader
          title="No autorizado"
          subtitle="Solo super administradores pueden gestionar administradores."
        />
      </PageContainer>
    );
  }

  const handleRevoke = async (userId: string, name: string) => {
    if (
      !confirm(
        `¿Quitar a ${name} como administrador? Conservará su acceso si pertenece a otros condominios.`,
      )
    )
      return;
    try {
      await revoke.mutateAsync(userId);
      toast.success('Administrador removido.');
    } catch (err: unknown) {
      const apiErr = err as { detail?: string } | null;
      toast.error(apiErr?.detail ?? 'No se pudo quitar al administrador.');
    }
  };

  return (
    <PageContainer width="wide">
      <PageHeader
        eyebrow="Configuración"
        title="Administradores"
        subtitle="Gestión de usuarios con rol admin para este condominio."
        action={
          <Button onClick={() => setInviteOpen(true)}>Invitar administrador</Button>
        }
      />
      {isLoading ? (
        <p className="text-sm text-[var(--color-ink-muted)]">Cargando…</p>
      ) : data?.items.length === 0 ? (
        <p className="text-sm text-[var(--color-ink-muted)]">Aún no hay administradores.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[var(--color-hairline)] text-left">
              <th className="eyebrow py-2">Nombre</th>
              <th className="eyebrow">Correo</th>
              <th className="eyebrow">Estado</th>
              <th className="eyebrow">Otorgado</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.items.map((m) => (
              <tr key={m.cognitoSub} className="border-b border-[var(--color-hairline)]">
                <td className="py-2">{m.displayName ?? '—'}</td>
                <td>{m.email}</td>
                <td>
                  <Badge>{m.status}</Badge>
                </td>
                <td className="tabular-nums">
                  {new Date(m.grantedAt).toLocaleDateString('es-CL')}
                </td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleRevoke(m.cognitoSub, m.displayName ?? m.email)}
                  >
                    Quitar
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <InviteModal tenantId={condoId} open={inviteOpen} onOpenChange={setInviteOpen} />
    </PageContainer>
  );
}
